/**
 * VariableMappingForm.jsx — Map template variables ({{1}}, {{2}}, ...) to JSON
 * keys returned by the BYO webhook response.
 *
 * Props:
 *   templateVariables:  string[]   e.g. ['1','2','3'] or ['name','order_id']
 *   detectedColumns:    string[]   from test-webhook response (auto-fills dropdown)
 *   value:              { [varKey]: string }
 *   onChange:           (mapping) => void
 *   reservedKeys:       string[]   e.g. ['mobile','countrycode','client_ref']
 */

import React from 'react';
import { Select, Tag, Tooltip, Alert } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, ExperimentOutlined, InfoCircleOutlined } from '@ant-design/icons';
import './VariableMappingForm.css';

// Backend uses 'mobile' as the required key (not 'phone')
const RESERVED_DEFAULT = ['mobile', 'full_name'];
const RESERVED_META = {
  mobile:    { label: 'mobile', desc: 'Recipient phone with country code, no +', required: true },
  full_name: { label: 'full_name', desc: 'Contact display name', required: false },
  countrycode: { label: 'countrycode', desc: 'Digits only, e.g. 91', required: false },
  client_ref:  { label: 'client_ref', desc: 'Your-side ID echoed in callbacks', required: false },
};

export default function VariableMappingForm({
  templateVariables = [],
  detectedColumns = [],
  value = {},
  onChange,
  reservedKeys = RESERVED_DEFAULT,
}) {
  const updateRow = (varKey, mapped) => {
    onChange?.({ ...value, [varKey]: mapped });
  };

  const hasDetected = detectedColumns.length > 0;
  const hasVariables = templateVariables.length > 0;

  return (
    <div className="vmf-wrap">

      {/* ── Variable mapping section ── */}
      {hasVariables && (
        <div className="vmf-section">
          <h4 className="vmf-title">
            Map Template Variables
            <Tooltip title="Each {{n}} placeholder in your template will be replaced by the JSON key you select here at send time.">
              <InfoCircleOutlined className="vmf-info" />
            </Tooltip>
          </h4>

          {/* Prompt to run test first */}
          {!hasDetected && (
            <Alert
              type="info"
              showIcon
              icon={<ExperimentOutlined />}
              message="Run Test Webhook first"
              description="Click 'Test Webhook' above to connect to your endpoint. The JSON keys your server returns will then appear in the dropdowns below."
              style={{ marginBottom: 12 }}
            />
          )}

          <div className="vmf-rows">
            {templateVariables.map((varKey) => (
              <div className="vmf-row" key={varKey}>
                <span className="vmf-var-pill">{`{{${varKey}}}`}</span>
                <span className="vmf-arrow">→</span>
                <Select
                  className="vmf-input"
                  value={value[varKey] || undefined}
                  onChange={(val) => updateRow(varKey, val)}
                  placeholder={hasDetected ? 'Select webhook key' : 'Run test to see keys'}
                  disabled={!hasDetected}
                  allowClear
                  showSearch
                  style={{ width: '100%' }}
                  options={detectedColumns.map((c) => ({ value: c, label: c }))}
                  filterOption={(input, opt) =>
                    (opt?.value || '').toLowerCase().includes((input || '').toLowerCase())
                  }
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Detected columns section (shown after test regardless of variables) ── */}
      <div className="vmf-section">
        <h4 className="vmf-title">
          Webhook Response Keys
          <Tooltip title="Keys your server returns in each data record. 'mobile' is required.">
            <InfoCircleOutlined className="vmf-info" />
          </Tooltip>
        </h4>

        {!hasDetected ? (
          <p className="vmf-help" style={{ color: '#9ca3af' }}>
            Run <strong>Test Webhook</strong> above to validate your endpoint and see which keys it returns.
          </p>
        ) : (
          <>
            {/* Show all detected keys with required/optional tagging */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {detectedColumns.map((col) => {
                const meta = RESERVED_META[col];
                const isRequired = meta?.required;
                const isKnown = !!meta;
                return (
                  <Tag
                    key={col}
                    color={isRequired ? 'green' : isKnown ? 'blue' : 'default'}
                    style={{ fontSize: 13, padding: '2px 10px' }}
                  >
                    {isRequired && <CheckCircleFilled style={{ marginRight: 4 }} />}
                    {col}
                    {isRequired && <span style={{ fontSize: 11, marginLeft: 4, opacity: 0.8 }}>required</span>}
                  </Tag>
                );
              })}
            </div>

            {/* Required key check */}
            <ul className="vmf-reserved">
              {reservedKeys.map((k) => {
                const present = detectedColumns.includes(k);
                const meta = RESERVED_META[k];
                return (
                  <li key={k} className="vmf-reserved-item">
                    {present
                      ? <CheckCircleFilled className="vmf-ok" />
                      : <CloseCircleFilled className="vmf-bad" />}
                    <span><code>{meta?.label || k}</code> — {meta?.desc || k}</span>
                    {meta?.required && <Tag color="red" className="vmf-tag">required</Tag>}
                    {!meta?.required && meta && <Tag color="gold" className="vmf-tag">recommended</Tag>}
                  </li>
                );
              })}
            </ul>

            {/* Not-variable template confirmation */}
            {!hasVariables && (
              <p className="vmf-help" style={{ marginTop: 8, color: '#52c41a' }}>
                ✓ This template has no variables — no field mapping needed.
                MessageOS will use the <code>mobile</code> key for routing.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
